import { useEffect, useState } from "react";
import { AUDIENCES, C, audiences, components, formats, mechanics, sliders, themes, type AudienceId, type Focus, type PartId, type SliderId, gameSizes } from "@bd/content";
import { baseUnitCost, defaultDesign, fairPrice, heat, priceCeiling, priceIndex, validateDesign, money, yearOf, focusSum, type Company, type Design, suggestGameName, estimateWeeks, dateOf } from "@bd/sim";
import { Icon, Sheet, Why } from "@bd/ui-kit";
import { t, tp, tdate } from "../i18n";
import { me, useGame } from "../store";
import { cover } from "../ui/Hud";
import { Fact, FitPill, PickCard, Picker, ClockIcon, type PickOption } from "./ConceptPicker";

const PART_LABEL: Record<PartId, string> = { cards: "Cards", board: "Board", dice: "Dice", tokens: "Tokens", minis: "Minis", box: "Box", rulebook: "Rulebook", insert: "Insert", extras: "Extras" };

/** A rules-engine message (English) in the player's language; the few with numbers or names in them are matched by shape. */
function issueText(m: string): string {
  let x = /^Spread exactly 10 focus points \(now (.+)\)\.$/.exec(m);
  if (x) return t("Spread exactly 10 focus points (now {n}).", { n: x[1] });
  x = /^(.+)s don't use (.+)\.$/.exec(m);
  if (x) return t("{format}s don't use {part}.",{ format: t(x[1]), part: t(PART_LABEL[x[2] as PartId] ?? x[2]).toLowerCase() });
  x = /^(.+) isn't unlocked yet\.$/.exec(m);
  if (x && !m.startsWith("That ")) return t("{name} isn't unlocked yet.", { name: t(x[1]) });
  return t(m);
}

/**
 * The design you were working on, kept when the desk closes (a stray tap above the sheet on a phone).
 * Cleared when you start the game.
 */
let draft: { key: string; design: Design; priceSet: boolean } | null = null;

export function DesignDesk({ projectId, licence, back }: { projectId?: string; licence?: string; back?: "playtest" }) {
  const game = useGame((s) => s.game)!;
  const set = useGame((s) => s.set);
  const cmd = useGame((s) => s.cmd);
  const toast = useGame((s) => s.toast);
  const onboarding = useGame((s) => s.onboarding);
  const co = me(game) as Company;
  const existing = projectId ? co.projects.find((p) => p.id === projectId) : undefined;
  const has = (id: string) => co.unlocked.includes(id);
  const year = yearOf(game.week);
  const draftKey = `${game.seed}:${co.id}:${projectId ?? licence ?? "new"}`;
  const [d, setD] = useState<Design>(() => {
    if (draft?.key === draftKey) return draft.design;
    if (existing) return JSON.parse(JSON.stringify(existing.design));
    const first = co.products.length === 0 && co.projects.length === 0;
    const L = licence ? C.licence[licence] : undefined;
    if (L) return defaultDesign(`${L.name}: The Game`, "set_collection", L.theme, L.audience, "card", game.week);
    // from your second game: a suggested name, and the focus you last used for this audience
    const nd = defaultDesign(first ? "Critter Quiz" : "", first ? "trivia" : "set_collection", first ? "wild_animals" : "gardens", "family", "card", game.week, lastFocus("family"));
    if (!first) nd.name = suggestGameName(co, nd.theme, co.products.length + co.projects.length);
    return nd;
  });
  const [err, setErr] = useState<string | null>(null);
  const [picking, setPicking] = useState<null | "mechanic" | "theme" | "audience" | "format" | "size">(null);
  const issues = validateDesign(d, has);
  const left = 10 - focusSum(d.focus);
  // until you set the price yourself, it follows what the game feels worth
  const [priceSet, setPriceSet] = useState(() => (draft?.key === draftKey ? draft.priceSet : !!projectId));
  useEffect(() => {
    draft = { key: draftKey, design: d, priceSet };
  }, [d, priceSet, draftKey]);
  const discard = () => {
    draft = null;
  };
  const upd = (p: Partial<Design>) => setD((x) => {
    const next = { ...x, ...p };
    if (!priceSet && p.price === undefined) next.price = Math.max(1, Math.round(fairPrice(next, game.week, 0.6, co.reputation) * 0.95 * 2) / 2);
    return next;
  });
  const fmt = C.format[d.format];

  const setFocus = (k: SliderId, delta: number) => {
    const v = d.focus[k] + delta;
    if (v < 0 || v > 5) return;
    if (delta > 0 && left <= 0) return;
    upd({ focus: { ...d.focus, [k]: v } as Focus });
  };

  const changeFormat = (f: string) => {
    const nd = defaultDesign(d.name, d.mechanic, d.theme, d.audience, f, game.week, d.focus);
    upd({ format: f, parts: nd.parts });
  };

  const artGuess = Math.max(0.2, Math.max(...co.staff.map((s) => s.stats.art)) / 100);
  const fair = fairPrice(d, game.week, artGuess, co.reputation);
  const ceiling = priceCeiling(d.audience, game.week, d.scope);
  const unit = baseUnitCost(d) * 1.6 * priceIndex(game.week);
  const mt = co.notebook[`mt:${d.mechanic}:${d.theme}`];
  const ma = co.notebook[`ma:${d.mechanic}:${d.audience}`];
  const ta = co.notebook[`ta:${d.theme}:${d.audience}`];
  const mechName = t(C.mechanic[d.mechanic].name);
  const themeName = t(C.theme[d.theme].name);
  const audName = t(C.audience[d.audience].name);

  const start = async () => {
    setErr(null);
    if (issues.length) return setErr(issues[0].message);
    const r = existing ? await cmd({ type: "editDesign", projectId: existing.id, design: d }) : await cmd({ type: "startProject", design: d, licence });
    if (!r.ok) return setErr(r.error);
    discard();
    rememberFocus(d.audience, d.focus);
    toast({ title: existing ? t("Design updated") : t("Work begins on {name}", { name: d.name }), text: existing ? undefined : t("Your team builds the prototype, then you playtest it."), tone: "good" });
    // came from the playtest table: go back to it, unless the change restarted the prototype
    const after = useGame.getState().game;
    const stillTesting = existing && back === "playtest" && me(after!)?.projects.find((x) => x.id === existing.id)?.phase === existing.phase;
    set({ screen: stillTesting ? { kind: "playtest", projectId: existing.id } : null, onboarding: Math.max(onboarding, 1) });
    if (!existing) useGame.getState().resume();
  };

  const parts = fmt.parts;

  const lockWhy = (yr: number) => (yr > year ? `${yr}` : t("Research"));
  const audPlayers = t("{audience} players", { audience: audName });
  const minutes = (min: number) => <Fact icon="clock" title={t("Up to {min} min", { min })}>{t("{n} min", { n: min })}</Fact>;
  const unusual = (f: string) => C.mechanic[d.mechanic].formats.includes(f) ? undefined : <Fact icon="warn" tone="warn" title={t("Unusual for {mechanic}", { mechanic: mechName.toLowerCase() })}>{t("Unusual")}</Fact>;
  const sizeFacts = (dd: Design, sales: number) => (
    <>
      <Fact icon="calendar" title={t("Weeks with your free team")}>{t("{n} wk", { n: estimateWeeks(co, dd, game.week) })}</Fact>
      {sales !== 1 && <Fact icon="trend" title={t("{x}× the sales", { x: sales })}>×{sales}</Fact>}
    </>
  );
  const pickOptions = (k: "mechanic" | "theme" | "audience" | "format" | "size"): PickOption[] => {
    if (k === "mechanic") return mechanics.filter((m) => !m.hidden || has(`m:${m.id}`)).map((m) => ({ id: m.id, name: t(m.name), hint: t(m.blurb), fit: co.notebook[`ma:${m.id}:${d.audience}`], fitTitle: audPlayers, locked: has(`m:${m.id}`) ? undefined : lockWhy(m.year) }));
    if (k === "theme") return themes.map((th) => ({ id: th.id, name: t(th.name), fit: co.notebook[`mt:${d.mechanic}:${th.id}`], fitTitle: t("With {mechanic}", { mechanic: mechName }), tag: heat(game, "theme", th.id) >= 0.3 ? t("Trending") : undefined, locked: has(`t:${th.id}`) ? undefined : lockWhy(th.year) }));
    if (k === "audience") return audiences.map((a) => ({ id: a.id, name: t(a.name), meta: minutes(a.playTimeLimit), fit: co.notebook[`ma:${d.mechanic}:${a.id}`], fitTitle: t("Like {mechanic}", { mechanic: mechName }), locked: has(`a:${a.id}`) ? undefined : `${a.year}` }));
    if (k === "size") {
      const order = ["garage", "office", "studio", "hq"];
      return gameSizes.map((z) => ({
        id: z.id, name: t(z.name), hint: t(z.blurb), meta: sizeFacts({ ...d, scope: z.id as Design["scope"] }, z.sales),
        locked: order.indexOf(co.level) < order.indexOf(z.level) ? t(C.level[z.level].name) : year < z.year ? `${z.year}` : undefined,
      }));
    }
    return formats.map((f) => ({ id: f.id, name: t(f.name), meta: unusual(f.id), locked: has(`f:${f.id}`) ? undefined : `${f.year}` }));
  };
  const choose = (k: "mechanic" | "theme" | "audience" | "format" | "size", id: string) => {
    if (k === "mechanic") {
      const okF = C.mechanic[id].formats.find((f) => has(`f:${f}`)) ?? d.format;
      const nd = defaultDesign(d.name, id, d.theme, d.audience, okF, game.week, d.focus);
      upd({ mechanic: id, format: okF, parts: nd.parts });
    } else if (k === "theme") {
      if (!licence) upd({ theme: id });
    } else if (k === "audience") {
      const aud = C.audience[id as AudienceId];
      const remembered = lastFocus(id as AudienceId);
      upd({ audience: id as AudienceId, players: [aud.players[0], Math.min(aud.players[1], 6)], playTime: Math.min(d.playTime, aud.playTimeLimit), ...(remembered && !existing ? { focus: remembered } : {}) });
    } else if (k === "size") {
      // a bigger box can carry a bigger price: keep the price where it sits against the new fair price
      const next = { ...d, scope: id as "filler" | "standard" | "big" | "epic" };
      const ratio = fairPrice(next, game.week, 0.6, co.reputation) / Math.max(0.01, fairPrice(d, game.week, 0.6, co.reputation));
      upd({ scope: next.scope, price: Math.max(1, Math.round(d.price * ratio * 2) / 2) });
    } else changeFormat(id);
  };

  const shownErr = err ?? issues[0]?.message;
  const close = () => set({ screen: existing && back === "playtest" ? { kind: "playtest", projectId: existing.id } : null });
  // a rework that changes the core restarts the prototype: say so only once it would happen
  const restarts = !!existing && (d.mechanic !== existing.design.mechanic || d.theme !== existing.design.theme || d.format !== existing.design.format);
  const L = licence ? C.licence[licence] : undefined;
  const title = existing ? t("Rework {name}", { name: existing.design.name }) : L ? t("Licensed game: {licence}", { licence: t(L.name) }) : t("Design desk");

  return (
    <Sheet title={L ? <span className="title-facts">{title} <Fact icon="calendar" title={tp(L.deadlineWeeks, "Theme set by the licence · {n} week to deliver", "Theme set by the licence · {n} weeks to deliver")}>{t("{n} wk", { n: L.deadlineWeeks })}</Fact></span> : title} onClose={close} wide testId="design-desk"
      footer={<>
        {shownErr ? <span className="bad dd-foot-note" role="alert">{issueText(shownErr)}</span>
          : restarts ? <span className="dd-foot-note"><Fact icon="replay" tone="warn" title={t("Changing mechanic, theme or format restarts the prototype")}>{t("Restarts prototype")}</Fact></span>
          : <span className="muted dd-foot-note"><Fact icon="print" title={t("About {cost} a copy at the local printer", { cost: money(unit) })}>{money(unit)}{t("/copy")}</Fact> <Why title={t("Cost per copy")} lines={[{ label: t("Parts"), value: money(baseUnitCost(d) * priceIndex(game.week)) }, { label: t("Local printer's price"), value: money(unit) }]} note={t("Bigger printers charge less per copy.")} /></span>}
        <button className="btn primary" onClick={start} disabled={issues.length > 0} data-testid="start-prototype">{existing ? t("Save changes") : t("Start prototype")}</button>
      </>}>
      {existing && !picking && <OrderAhead projectId={existing.id} />}
      {picking ? (
        <Picker
          title={{ mechanic: t("Choose a mechanic"), theme: t("Choose a theme"), audience: t("Choose an audience"), format: t("Choose a format"), size: t("Choose a size") }[picking]}
          fitLabelText={picking === "mechanic" ? t("Fit with {audience} players", { audience: audName }) : picking === "theme" ? t("Fit with {mechanic}", { mechanic: mechName }) : picking === "audience" ? t("How much they like {mechanic}", { mechanic: mechName }) : undefined}
          options={pickOptions(picking)}
          value={{ mechanic: d.mechanic, theme: d.theme, audience: d.audience, format: d.format, size: d.scope ?? "standard" }[picking]}
          onPick={(id) => { choose(picking, id); setPicking(null); }}
          onClose={() => setPicking(null)}
          testId={{ mechanic: "mechanics", theme: "themes", audience: "audiences", format: "formats", size: "sizes" }[picking]}
        />
      ) : (
      <div className="design-grid">
        {/* ---------------------------------------------------- concept: the box, its name, and five picks, each opening its own list */}
        <section className="stack">
          <div className="field dd-name">
            <span className="dd-cover" title={t("{mechanic} with {theme}", { mechanic: mechName, theme: themeName.toLowerCase() })}>
              <img src={cover(d.name || C.theme[d.theme].name, d.theme, d.mechanic, co.color, 0.6, 1, false, d.audience)} alt={t("Box cover preview")} width={96} height={72} />
              <FitPill n={mt} title={t("{mechanic} with {theme}", { mechanic: mechName, theme: themeName.toLowerCase() })} />
            </span>
            <input value={d.name} maxLength={32} placeholder={t("Name your game")} aria-label={t("Name")} onChange={(e) => upd({ name: e.target.value })} data-testid="game-name" />
            <button className="icon-btn" onClick={() => upd({ name: suggestGameName(co, d.theme, co.products.length + co.projects.length + ++nameTurn) })} data-testid="suggest-name" aria-label={t("Suggest a name")} title={t("Suggest a name")}><Icon name="dice" size={20} /></button>
          </div>
          <div className="pick-grid">
            <PickCard label={t("Mechanic")} value={mechName} fit={ma} fitTitle={audPlayers} onOpen={() => setPicking("mechanic")} testId="pick-mechanic" />
            <PickCard label={t("Theme")} value={themeName} fit={ta} fitTitle={audPlayers} onOpen={() => setPicking("theme")} disabled={!!licence} meta={licence ? <Fact icon="lock" title={t("Set by the licence")}>{t("Licence")}</Fact> : undefined} testId="pick-theme" />
            <PickCard label={t("Audience")} value={audName} meta={<>{minutes(C.audience[d.audience].playTimeLimit)}<Fact icon="coin" title={t("Pays up to {price}", { price: money(Math.round(ceiling * 2) / 2) })}>{money(Math.round(ceiling * 2) / 2)}</Fact></>} onOpen={() => setPicking("audience")} testId="pick-audience" />
            <PickCard label={t("Format")} value={t(fmt.name)} meta={unusual(d.format)} onOpen={() => setPicking("format")} testId="pick-format" />
            <PickCard label={t("Size")} value={t(C.size[d.scope ?? "standard"].name)} title={t(C.size[d.scope ?? "standard"].blurb)} meta={sizeFacts(d, 1)} onOpen={() => setPicking("size")} disabled={!!existing} testId="pick-size" />
          </div>
        </section>

        {/* ---------------------------------------------------- focus */}
        <section className="stack">
          <div className="spread">
            <span className="caps" title={t("Each audience likes a different mix; testers and reviewers hint at it.")}>{t("Focus")}</span>
            <span className={`fact-chip num ${left === 0 ? "good" : left < 0 ? "bad" : ""}`} data-testid="focus-left" title={t("{left} of 10 points left", { left })}>{left}/10</span>
          </div>
          {sliders.map((s) => (
            <div key={s.id} className="focus-row" title={t("Good for: {pushes}. Costs: {costs}.", { pushes: t(s.pushes), costs: t(s.costs) })}>
              <span className="focus-name">{t(s.name)}</span>
              <button className="icon-btn step" onClick={() => setFocus(s.id, -1)} aria-label={t("Less {slider}", { slider: t(s.name) })} disabled={d.focus[s.id] <= 0}><Icon name="minus" size={16} /></button>
              <span className="pips" aria-label={t("{slider} {n} of 5", { slider: t(s.name), n: d.focus[s.id] })}>
                {[0, 1, 2, 3, 4].map((i) => <i key={i} className={i < d.focus[s.id] ? "on" : ""} />)}
              </span>
              <button className="icon-btn step" onClick={() => setFocus(s.id, 1)} aria-label={t("More {slider}", { slider: t(s.name) })} disabled={left <= 0 || d.focus[s.id] >= 5} data-testid={`more-${s.id}`}><Icon name="plus" size={16} /></button>
            </div>
          ))}
          <div className="dd-shape">
            <span className="dd-players" title={t("Players")}>
              <Icon name="staff" size={18} color="var(--muted)" />
              <select value={d.players[0]} onChange={(e) => upd({ players: [Number(e.target.value), Math.max(Number(e.target.value), d.players[1])] })} aria-label={t("Fewest players")}>
                {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => <option key={n}>{n}</option>)}
              </select>
              –
              <select value={d.players[1]} onChange={(e) => upd({ players: [Math.min(d.players[0], Number(e.target.value)), Number(e.target.value)] })} aria-label={t("Most players")}>
                {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => <option key={n}>{n}</option>)}
              </select>
            </span>
            <label className="dd-time">
              <span className={`fact${d.playTime > C.audience[d.audience].playTimeLimit ? " warnc" : ""}`} title={d.playTime > C.audience[d.audience].playTimeLimit ? t("(long for {audience})", { audience: audName }) : t("Play time")}><ClockIcon size={16} />{t("{n} min", { n: d.playTime })}</span>
              <input type="range" className="slider" min={10} max={240} step={5} value={d.playTime} onChange={(e) => upd({ playTime: Number(e.target.value) })} aria-label={t("Play time")} />
            </label>
          </div>
        </section>

        {/* ---------------------------------------------------- components and price */}
        <section className="stack">
          {parts.map((part) => {
            const opts = components.filter((c) => c.part === part);
            return (
              <label key={part} className="part-row">
                <span>{t(PART_LABEL[part])}</span>
                <select value={d.parts[part] ?? ""} onChange={(e) => upd({ parts: { ...d.parts, [part]: e.target.value } })} aria-label={t(PART_LABEL[part])}>
                  {opts.map((o) => {
                    const ok = o.tier === 0 || has(`c:${o.id}`);
                    return <option key={o.id} value={o.id} disabled={!ok}>{t(o.name)}{!ok ? (o.year > year ? ` (${o.year})` : ` (${t("research", undefined, "verb")})`) : o.tier > 0 ? ` · ${money(o.cost * 0.3 * 1.6 * priceIndex(game.week))}` : ""}</option>;
                  })}
                </select>
              </label>
            );
          })}
          <div className="inset" style={{ padding: 12, marginTop: 6 }}>
            <div className="spread">
              <span className="num" style={{ fontSize: "1.6rem" }} title={t("Price")}>{money(d.price)}</span>
              <span className="row" style={{ gap: 6 }}>
                {priceSet && Math.abs(d.price - Math.round(fair * 0.95 * 2) / 2) >= 0.5 && <button className="icon-btn" onClick={() => { setPriceSet(false); upd({ price: Math.max(1, Math.round(fair * 0.95 * 2) / 2) }); }} data-testid="price-reset" aria-label={t("Reset to a fair price")} title={t("Reset to a fair price")}><Icon name="replay" size={18} /></button>}
                <span className="muted" style={{ fontSize: "0.85rem" }} title={t("Feels worth about {price}", { price: money(fair) })}>≈ {money(fair)}</span>
                <Why title={t("What players think it's worth")} lines={[{ label: t("Box and parts"), value: money(fair) }, { label: t("Most {audience} players pay", { audience: audName }), value: money(ceiling) }]} note={t("Better art and reputation raise it.")} />
              </span>
            </div>
            <input type="range" className="slider" min={Math.max(0.5, fair * 0.4)} max={Math.max(fair * 2, ceiling * 1.3)} step={0.5} value={d.price} onChange={(e) => { setPriceSet(true); upd({ price: Number(e.target.value) }); }} aria-label={t("Price")} data-testid="price" />
            <PriceHint price={d.price} fair={fair} ceiling={ceiling} unit={unit} />
          </div>
        </section>
      </div>
      )}
    </Sheet>
  );
}

function PriceHint({ price, fair, ceiling, unit }: { price: number; fair: number; ceiling: number; unit: number }) {
  const margin = price * 0.42 - unit;
  const [word, tone, why]: [string, "bad" | "warn" | "good", string] = price > ceiling * 1.05 ? [t("Too high"), "bad", t("Over what this audience pays: sales drop hard.")] : price > fair * 1.25 ? [t("Pricey"), "warn", t("Pricey: fewer buyers.")] : price < fair * 0.75 ? [t("Bargain"), "warn", t("A bargain: more buyers, thin margin.")] : [t("Fair"), "good", t("A fair price.")];
  return (
    <div className="facts" style={{ marginTop: 6 }}>
      <span className={`fact-chip ${tone === "warn" ? "warnc" : tone}`} title={why}>{word}</span>
      <Fact icon="coin" tone={margin < 0 ? "bad" : undefined} title={t("You keep about {money} a copy", { money: money(margin) })}>{money(margin)}{t("/copy")}</Fact>
    </div>
  );
}

export { AUDIENCES };

let nameTurn = 0;

/** Pre-orders building up, and the crowdfunding campaign (once you have the BackerBox channel). */
function OrderAhead({ projectId }: { projectId: string }) {
  const game = useGame((s) => s.game)!;
  const cmd = useGame((s) => s.cmd);
  const toast = useGame((s) => s.toast);
  const co = me(game)!;
  const p = co.projects.find((x) => x.id === projectId);
  if (!p) return null;
  const canCrowd = co.unlocked.includes("ch:backerbox");
  const c = p.crowd;
  // nobody to order ahead yet: the block would only say "0 pre-orders"
  if (!canCrowd && !c && (p.preorders ?? 0) === 0 && co.fans < 100) return null;
  const n = (x: number) => x.toLocaleString("en-US");
  return (
    <div className="card dd-ahead" data-testid="order-ahead">
      <Fact icon="fans" title={t("Fans pre-order during art and rules; more hype, more orders. They ship first on launch day: print enough.")}>{tp(p.preorders ?? 0, "{n} pre-order", "{n} pre-orders")}</Fact>
      {canCrowd && !c && p.phase !== "prototype" && (
        <button className="btn small primary" onClick={async () => { const r = await cmd({ type: "crowdfund", projectId: p.id }); if (!r.ok) toast({ title: t("Not yet"), text: t(r.error), tone: "warn" }); }} data-testid="crowdfund" title={t("4 weeks on BackerBox. Hit the goal: backers pay up front (minus a 7% fee) and add hype. Miss it: nobody pays. Ship within 30 weeks or they'll remember.")}>{t("Crowdfund")}</button>
      )}
      {canCrowd && <Why title={t("Crowdfund")} lines={[]} note={t("4 weeks on BackerBox. Hit the goal: backers pay up front (minus a 7% fee) and add hype. Miss it: nobody pays. Ship within 30 weeks or they'll remember.")} />}
      {c && !c.done && <Fact icon="coin" title={t("Campaign live: {backers} of {goal} backers, ends in {time}.", { backers: n(c.backers), goal: n(c.goal ?? 0), time: tp(Math.max(0, c.endsWeek - game.week), "{n} week", "{n} weeks") })}>{n(c.backers)}/{n(c.goal ?? 0)} · {t("{n} wk", { n: Math.max(0, c.endsWeek - game.week) })}</Fact>}
      {c?.done && c.funded && <Fact icon="check" tone={game.week > c.deadline ? "bad" : "good"} title={`${t("Funded: {backers} backers paid. Due by {date}.", { backers: n(c.backers), date: tdate(dateOf(c.deadline).label) })} ${t("Cancelling means refunds and a hit to your reputation.")}`}>{n(c.backers)} · {t("due {date}", { date: tdate(dateOf(c.deadline).label) })}{game.week > c.deadline ? ` · ${t("Late!")}` : ""}</Fact>}
      {c?.done && !c.funded && <Fact icon="close" title={t("Campaign fell short ({backers} of {goal}). Nobody was charged.", { backers: n(c.backers), goal: n(c.goal ?? 0) })}>{n(c.backers)}/{n(c.goal ?? 0)}</Fact>}
    </div>
  );
}

/** The focus you last used for an audience (kept on this device), so what you learned carries to the next game. */
function lastFocus(aud: AudienceId): Focus | undefined {
  try {
    const all = JSON.parse(localStorage.getItem("bd:focus") ?? "{}") as Record<string, Focus>;
    return all[aud];
  } catch {
    return undefined;
  }
}

function rememberFocus(aud: AudienceId, f: Focus): void {
  try {
    const all = JSON.parse(localStorage.getItem("bd:focus") ?? "{}") as Record<string, Focus>;
    all[aud] = f;
    localStorage.setItem("bd:focus", JSON.stringify(all));
  } catch {
    // storage blocked: the desk starts from the default focus next time
  }
}
